import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';

import { useLocalStorage } from '../../auth/useLocalStorage';
import { headerData } from '../../data/headerCostum';
import { useReactToPrint } from 'react-to-print';

// Styled components untuk tampilan yang konsisten

const PrintPDF = ({ execute }) => {
  const [longwis] = useLocalStorage("longwis");
  const [dataLongwis, setDataLongwis] = useState(null);
  const [dokumentasiCCTV, setdokumentasiCCTV] = useState(null);
  const [dokumentasiNVR, setdokumentasiNVR] = useState(null);
  const [dokumentasiHarddisk, setdokumentasiHarddisk] = useState(null);
  const [dokumentasiSwitch, setdokumentasiSwitch] = useState(null);
  const [dokumentasiLayar, setdokumentasiLayar] = useState(null);
  const [dokumentasiSistem, setdokumentasiSistem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloaded, setDownloaded] = useState(false);

  const pdfRef = useRef();

  const DownloadPDF = useReactToPrint({
    content: () => pdfRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 20mm;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; }
      }
    `
  });


  const statusConverter = (status, type) => {
    // Handle null/undefined status
    if (status === null || status === undefined) {
      return "Data tidak tersedia";
    }
    
    // Convert to number if it's a string
    const numStatus = typeof status === 'string' ? parseInt(status) : status;
    
    if (type === "tampilan") {
      switch (numStatus) {
        case 0: return "Tampilan Tidak Lengkap";
        case 1: return "Tampilan Lengkap";
        default: return "Status tidak dikenali";
      }
    } else if (type === "sistem") {
      switch (numStatus) {
        case 0: return "Tampilan System Offline";
        case 1: return "Tampilan System Online";
        default: return "Status tidak dikenali";
      }
    } else {
      switch (numStatus) {
        case 0: return "Fisik Belum Terpasang";
        case 1: return "Fisik Terpasang Sebagian";
        case 2: return "Fisik Terpasang Semua";
        default: return "Status tidak dikenali";
      }
    }
  };

  // Fungsi untuk safely access nested object properties
  const safeAccess = (obj, path, defaultValue = "-") => {
    if (!obj) return defaultValue;
    
    const keys = path.split('.');
    let result = obj;
    
    for (const key of keys) {
      if (result === null || result === undefined || !(key in result)) {
        return defaultValue;
      }
      result = result[key];
    }
    
    return result !== null && result !== undefined ? result : defaultValue;
  };

  // const formatDate = (value) => {
  //   const date_time = new Date(value);
  //   return (date_time.toISOString().split('T')[0]);
  // }


  const getLaporanData = useCallback(() => {
    setLoading(true);
    setError(null);
    
    fetch(process.env.REACT_APP_API_URL + "api/longwis/" + longwis?.id, {
      method: "get",
      headers: headerData
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Gagal mengambil data laporan');
        }
        return res.json();
      })
      .then((res) => {
        if (res.nama_longwis) {
          setDataLongwis(res);
          
          // Safe parsing untuk dokumentasi
          try {
            if (res.DataCCTV?.file_foto) {
              setdokumentasiCCTV(JSON.parse(res.DataCCTV.file_foto));
            }
            if (res.DataNVR?.file_foto) {
              setdokumentasiNVR(JSON.parse(res.DataNVR.file_foto));
            }
            if (res.Harddisk?.file_foto) {
              setdokumentasiHarddisk(JSON.parse(res.Harddisk.file_foto));
            }
            if (res.Switch?.file_foto) {
              setdokumentasiSwitch(JSON.parse(res.Switch.file_foto));
            }
            if (res.DataTampilanLayar?.file_foto) {
              setdokumentasiLayar(JSON.parse(res.DataTampilanLayar.file_foto));
            }
            if (res.DataTampilanSystem?.file_foto) {
              setdokumentasiSistem(JSON.parse(res.DataTampilanSystem.file_foto));
            }
          } catch (parseError) {
            console.error('Error parsing dokumentasi:', parseError);
          }
        } else {
          throw new Error('Data laporan tidak ditemukan');
        }
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setError(err.message || 'Terjadi kesalahan saat mengambil data');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [longwis?.id]);

  useEffect(() => {
    if (longwis?.id && !dataLongwis) {
      getLaporanData();
    } else if (dataLongwis && execute && !downloaded) {
      DownloadPDF();
      setDownloaded(true);
    }
  }, [dataLongwis, getLaporanData, DownloadPDF, downloaded, execute, longwis?.id]);

  // Loading state
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} sx={{ color: '#1dc39f' }} />
        <Typography variant="h6" color="text.secondary">
          Memuat data laporan...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ borderRadius: '12px' }}>
          <Typography variant="h6" gutterBottom>
            Terjadi Kesalahan
          </Typography>
          <Typography variant="body1">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // No data state
  if (!dataLongwis) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
          <Typography variant="h6" gutterBottom>
            Data Tidak Ditemukan
          </Typography>
          <Typography variant="body1">
            Data laporan tidak tersedia.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <div ref={pdfRef}>
      <Grid container width={"297mm"}>
        {/* <Grid item container direction={"row"} sx={{
          marginX: 20,
          marginY: 5
        }}>
          <Grid item container xs={3} justifyContent={"center"} >
            <img src={Logo} width={110} alt='Logo Pemkot' />
          </Grid>
          <Grid item xs={9} container direction={'column'} alignItems={"center"} justifyContent={"center"}>
            <Grid item>
              <Typography variant='h3' fontSize={35} textTransform={'uppercase'} fontWeight={'700'}>
                {"Pemerintah Kota Makassar"}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant='h5' fontSize={19} align='center' textTransform={'uppercase'} fontWeight={'700'}>
                {"Dinas Komunikasi dan Informatika Kota Makassar"}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant='body1'>
                {"Jalan Andi Pangeran Pettarani No. 62 Makassar 9032"}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant='body1'>
                {"Email: diskolminfo@makassarkota.go.id, HomePage: diskominfo.makassarkota.go.id"}
              </Typography>
            </Grid>
          </Grid>
        </Grid> */}
        <Grid item sx={{
          background: "GrayText",
          height: "2px",
          width: "100%",
          marginBottom: 1,
          marginX: 10,
        }}>
          <Divider />
        </Grid>
      </Grid>
      {/* <Box height={50} sx={{
      pageBreakBefore: "always"
    }} /> */}
      {
        dataLongwis
          ? <Grid item width={"297mm"} container paddingX={10}>
            <Grid container >
              <Grid item container spacing={2}>
                <Grid item>
                  Nama PIC: {safeAccess(dataLongwis, 'nama_pic')}
                </Grid>
                <Grid item>
                  No Telp PIC: {safeAccess(dataLongwis, 'kontak_pic')}
                </Grid>
                <Grid item>
                  Nama Longwis: {safeAccess(dataLongwis, 'nama_longwis')}
                </Grid>
                <Grid item>
                  Kecamatan: {safeAccess(dataLongwis, 'kecamatan')}
                </Grid>
                <Grid item>
                  Kelurahan: {safeAccess(dataLongwis, 'kelurahan')}
                </Grid>
                <Grid item>
                  Alamat: {safeAccess(dataLongwis, 'alamat')}
                </Grid>
                <Grid item>
                  Tanggal Pengecekan: {new Date().toLocaleDateString('id-ID')}
                </Grid>
              </Grid>
            </Grid>
            <Grid item sx={{
              background: "GrayText",
              height: "2px",
              width: "100%",
              marginBottom: 1,
              marginX: 10,
            }}></Grid>
            <TableContainer sx={{
              marginTop: 2
            }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>
                      <Typography fontWeight={700}>{"Jenis Pengecekan"}</Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Typography fontWeight={700}>{"Status"}</Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Typography fontWeight={700}>{"Keterangan"}</Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Typography fontWeight={700}>{"Dokumentasi"}</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography>
                        {'CCTV'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {statusConverter(safeAccess(dataLongwis, 'DataCCTV.status_fisik'))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {safeAccess(dataLongwis, 'DataCCTV.keterangan')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Grid container direction={"row"} spacing={1}>
                        {
                          dokumentasiCCTV
                            ? dokumentasiCCTV.map((element, index) => {
                              return (
                                <Grid key={index} item xs={6}>
                                  <img height={"150px"} width={"100%"} alt={element} src={process.env.REACT_APP_API_URL + 'uploads/cctv/' + element} />
                                </Grid>
                              );
                            })
                            : <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Tidak ada dokumentasi
                                </Typography>
                              </Grid>
                        }
                      </Grid>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography>
                        {'NVR'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {statusConverter(safeAccess(dataLongwis, 'DataNVR.status_fisik'))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {safeAccess(dataLongwis, 'DataNVR.keterangan')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Grid container direction={"row"} spacing={1}>
                        {
                          dokumentasiNVR
                            ? dokumentasiNVR.map((element, index) => {
                              return (
                                <Grid key={index} item xs={6}>
                                  <img height={"150px"} width={"100%"} alt={element} src={process.env.REACT_APP_API_URL + 'uploads/nvr/' + element} />
                                </Grid>
                              );
                            })
                            : <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Tidak ada dokumentasi
                                </Typography>
                              </Grid>
                        }
                      </Grid>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography>
                        {'Harddisk'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {statusConverter(safeAccess(dataLongwis, 'Harddisk.status_fisik'))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {safeAccess(dataLongwis, 'Harddisk.keterangan')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Grid container direction={"row"} spacing={1}>
                        {
                          dokumentasiHarddisk
                            ? dokumentasiHarddisk.map((element, index) => {
                              return (
                                <Grid key={index} item xs={6}>
                                  <img height={"150px"} width={"100%"} alt={element} src={process.env.REACT_APP_API_URL + 'uploads/harddisk/' + element} />
                                </Grid>
                              );
                            })
                            : <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Tidak ada dokumentasi
                                </Typography>
                              </Grid>
                        }
                      </Grid>
                    </TableCell>
                  </TableRow>


                  <TableRow>
                    <TableCell>
                      <Typography>
                        {'Switch'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {statusConverter(safeAccess(dataLongwis, 'Switch.status_fisik'))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {safeAccess(dataLongwis, 'Switch.keterangan')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Grid container direction={"row"} spacing={1}>
                        {
                          dokumentasiSwitch
                            ? dokumentasiSwitch.map((element, index) => {
                              return (
                                <Grid key={index} item xs={6}>
                                  <img height={"150px"} width={"100%"} alt={element} src={process.env.REACT_APP_API_URL + 'uploads/switch/' + element} />
                                </Grid>
                              );
                            })
                            : <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Tidak ada dokumentasi
                                </Typography>
                              </Grid>
                        }
                      </Grid>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography>
                        {'Layar'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {statusConverter(safeAccess(dataLongwis, 'DataTampilanLayar.status_tampilan'), 'tampilan')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {safeAccess(dataLongwis, 'DataTampilanLayar.keterangan')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Grid container direction={"row"} spacing={1}>
                        {
                          dokumentasiLayar
                            ? dokumentasiLayar.map((element, index) => {
                              return (
                                <Grid key={index} item xs={6}>
                                  <img height={"150px"} width={"100%"} alt={element} src={process.env.REACT_APP_API_URL + 'uploads/tampilan/' + element} />
                                </Grid>
                              );
                            })
                            : <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Tidak ada dokumentasi
                                </Typography>
                              </Grid>
                        }
                      </Grid>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography>
                        {'Sistem'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {statusConverter(safeAccess(dataLongwis, 'DataTampilanSystem.status_tampilan'), 'sistem')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {safeAccess(dataLongwis, 'DataTampilanSystem.keterangan')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Grid container direction={"row"} spacing={1}>
                        {
                          dokumentasiSistem
                            ? dokumentasiSistem.map((element, index) => {
                              return (
                                <Grid key={index} item xs={6}>
                                  <img height={"150px"} width={"100%"} alt={element} src={process.env.REACT_APP_API_URL + 'uploads/sistem/' + element} />
                                </Grid>
                              );
                            })
                            : <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Tidak ada dokumentasi
                                </Typography>
                              </Grid>
                        }
                      </Grid>
                    </TableCell>
                  </TableRow>


                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          : <></>
      }
    </div>
  );
}

export default PrintPDF;
